import { FormElementEnum, RICSchema } from '../ricFormTypes';
import { ProductCategory } from '../../penTypes';
import { FinalFeeType } from '../../financingTypes';

const CAFormSchema: RICSchema = {
  metadata: {
    feeTypeList: [
      FinalFeeType.DOCUMENT_PROCESSING,
      FinalFeeType.CALIFORNIA_TIRE,
      FinalFeeType.ELECTRONIC_REGISTRATION,
      FinalFeeType.EMISSIONS_TESTING,
      FinalFeeType.VEHICLE_LICENSE,
      FinalFeeType.REGISTRATION,
    ],
    numGapProducts: 1,
    numOtherDownpaymentElements: 3,
    numOtherFees: 1,
    numOtherNonTaxableCharges: 2,
    numServiceContracts: 5,
    numSurfaceProtectionProducts: 2,
    numTheftDeterrentDevices: 3,
    numTradeIns: 2,
  },
  elements: [
    {
      type: FormElementEnum.FORM_TITLE,
      title: 'Itemization of Amount Financed',
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Vehicle Information',
      sectionElements: [
        {
          type: FormElementEnum.STRING_INPUT,
          title: 'Stock',
          responseDataField: 'carStock',
          required: true,
        },
        {
          type: FormElementEnum.INTEGER_INPUT,
          title: 'Mileage',
          responseDataField: 'mileage',
          required: true,
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Total Cash Price',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
        otherCharges: ['otherNonTaxableCharges'],
        productCategories: [
          ProductCategory.SURFACE_PROTECTION_PRODUCT,
          ProductCategory.THEFT_DETERRENT_DEVICE,
          ProductCategory.SERVICE_CONTRACT,
          ProductCategory.GAP,
        ],
        feeTypes: [
          FinalFeeType.DOCUMENT_PROCESSING,
          FinalFeeType.EMISSIONS_TESTING,
          FinalFeeType.ELECTRONIC_REGISTRATION,
        ],
        downPaymentPayoff: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.SUBSECTION,
          title: 'Cash Price of Motor Vehicle and Accessories',
          sumValue: {
            responseDataFields: ['effectivePrice', 'accessoriesPrice'],
            otherCharges: ['otherNonTaxableCharges'],
          },
          subSectionElements: [
            {
              type: FormElementEnum.PRICE_INPUT,
              title: 'Cash Price of Motor Vehicle',
              responseDataField: 'effectivePrice',
              required: true,
            },
            {
              type: FormElementEnum.PRICE_INPUT,
              title: 'Cash Price of Accessories',
              responseDataField: 'accessoriesPrice',
            },
            {
              type: FormElementEnum.CHARGE_TABLE,
              title: 'Other',
              subtitle: '(non-taxable)',
              displayTotalValue: false,
              responseDataField: 'otherNonTaxableCharges',
              columns: [
                {
                  width: '75%',
                  fieldName: 'description',
                },
              ],
            },
          ],
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Document Processing Charge',
          subtitle: '(not a government fee)',
          feeType: FinalFeeType.DOCUMENT_PROCESSING,
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Emissions Testing',
          subtitle: '(not a government fee)',
          feeType: FinalFeeType.EMISSIONS_TESTING,
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Theft Deterrent Devices',
          subtitle: '(optional)',
          displayTotalValue: true,
          productCategory: ProductCategory.THEFT_DETERRENT_DEVICE,
          columns: [{ width: '75%', fieldName: 'description' }],
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Surface Protection Products',
          subtitle: '(optional)',
          displayTotalValue: true,
          productCategory: ProductCategory.SURFACE_PROTECTION_PRODUCT,
          columns: [{ width: '75%', fieldName: 'description' }],
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Sales Tax',
          subtitle: '(On Taxable Items Listed Above)',
          responseDataField: 'taxAmountOverride',
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Debt Cancellation Agreement',
          subtitle: '(optional)',
          displayTotalValue: true,
          productCategory: ProductCategory.GAP,
          columns: [
            {
              title: 'Name',
              width: '40%',
              fieldName: 'description',
            },
            {
              title: 'Term (months)',
              width: '20%',
              fieldName: 'months',
            },
          ],
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Electronic Vehicle registration or Transfer Charge',
          subtitle: '(not a government fee)',
          displayTotalValue: false,
          feeType: FinalFeeType.ELECTRONIC_REGISTRATION,
          columns: [
            {
              title: 'Name',
              width: '75%',
              fieldName: 'paidTo',
            },
          ],
        },
        {
          type: FormElementEnum.PRODUCT_TABLE,
          title: 'Service Contract(s)',
          subtitle: '(optional)',
          displayTotalValue: true,
          productCategory: ProductCategory.SERVICE_CONTRACT,
          columns: [
            {
              title: 'Name',
              width: '40%',
              fieldName: 'description',
            },
            {
              title: 'Term (months)',
              width: '20%',
              fieldName: 'months',
            },
            {
              title: 'Miles',
              width: '20%',
              fieldName: 'miles',
            },
          ],
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Prior Credit or Lease Balance(s) Paid by Seller to',
          subtitle: '(See downpayment and trade-in calculation)',
          tradeInField: 'currentLienHolder',
          sumValue: {
            downPaymentPayoff: true,
          },
          required: true,
        },
      ],
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Amounts Paid to Public Officials',
      sumValue: {
        feeTypes: [
          FinalFeeType.VEHICLE_LICENSE,
          FinalFeeType.REGISTRATION,
          FinalFeeType.CALIFORNIA_TIRE,
          FinalFeeType.OTHER,
        ],
      },
      sectionElements: [
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Vehicle License Fees',
          subtitle: '(Estimated)',
          feeType: FinalFeeType.VEHICLE_LICENSE,
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'Registration/Transfer/Titling Fees',
          feeType: FinalFeeType.REGISTRATION,
        },
        {
          type: FormElementEnum.FEE_INPUT,
          title: 'California Tire Fees',
          feeType: FinalFeeType.CALIFORNIA_TIRE,
        },
        {
          type: FormElementEnum.FEE_TABLE,
          title: 'Other Fees',
          feeType: FinalFeeType.OTHER,
          displayTotalValue: false,
          columns: [
            {
              width: '75%',
              fieldName: 'description',
              title: 'Description',
            },
          ],
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Amounts Paid to Insurance Companies',
      subtitle: '(Total Premiums from Statement of Insurance)',
    },
    {
      type: FormElementEnum.FEE_DROPDOWN,
      title: 'Emission Fees',
      feeTypes: [
        FinalFeeType.EMISSIONS_EXEMPTION,
        FinalFeeType.EMISSIONS_CERTIFICATION,
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Subtotal For Above Fields',
      sumValue: {
        responseDataFields: [
          'effectivePrice',
          'accessoriesPrice',
          'taxAmountOverride',
        ],
        otherCharges: ['otherNonTaxableCharges'],
        productCategories: [
          ProductCategory.SURFACE_PROTECTION_PRODUCT,
          ProductCategory.THEFT_DETERRENT_DEVICE,
          ProductCategory.SERVICE_CONTRACT,
          ProductCategory.GAP,
        ],
        feeTypes: [
          FinalFeeType.DOCUMENT_PROCESSING,
          FinalFeeType.EMISSIONS_TESTING,
          FinalFeeType.ELECTRONIC_REGISTRATION,
          FinalFeeType.EMISSIONS_EXEMPTION,
          FinalFeeType.EMISSIONS_CERTIFICATION,
          FinalFeeType.VEHICLE_LICENSE,
          FinalFeeType.REGISTRATION,
          FinalFeeType.CALIFORNIA_TIRE,
          FinalFeeType.OTHER,
        ],
        downPaymentPayoff: true,
      },
    },
    {
      type: FormElementEnum.SECTION,
      title: 'Total Downpayment',
      sumValue: {
        downPaymentWithMinZero: true,
      },
      sectionElements: [
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle VIN(s)',
          tradeInField: 'id',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Year(s)',
          tradeInField: 'year',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Make(s)',
          tradeInField: 'make',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Model(s)',
          tradeInField: 'model',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Trade-in Vehicle Mileage(s)',
          tradeInField: 'mileage',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Total Agreed Value of Property Being Traded-In',
          subtitle: '(see trade-in vehicle(s))',
          tradeInField: 'tradeInAllowance',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Total Less Prior Credit or Lease Balance(s)',
          tradeInField: 'payOff',
          required: true,
        },
        {
          type: FormElementEnum.TRADE_IN_INPUT,
          title: 'Total Net Trade-In',
          subtitle: '(Indicate Negative Numbers)',
          tradeInField: 'netTrade',
          required: true,
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Deferred Downpayment Payable to Seller',
          responseDataField: 'deferredDownPayment',
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Manufacturers Rebate',
          responseDataField: 'rebate',
        },
        {
          type: FormElementEnum.CHARGE_TABLE,
          title: 'Other',
          responseDataField: 'otherDownPayments',
          displayTotalValue: false,
          columns: [
            {
              width: '75%',
              fieldName: 'description',
            },
          ],
        },
        {
          type: FormElementEnum.PRICE_INPUT,
          title: 'Cash, Cash Equivalent, Check, Credit Card, Or Debit Card',
          responseDataField: 'downPayment',
        },
      ],
    },
    {
      type: FormElementEnum.LINE_ITEM,
      title: 'Amount Financed',
      responseDataField: 'financeAmount',
    },
  ],
};

export default CAFormSchema;
